import React, { useEffect, useState } from "react";
import { Button } from "antd";
import useRecipients from "../../hooks/useRecipients";
import RecipientsTable from "../../components/Tables/RecipientsTable/RecipientsTable";
import GenericFormModal from "../../components/Modals/GenericFormModal/GenericFormModal";
import RecipientForm from "../../components/Forms/RecipientForm/RecipientForm";
import RecipientsMassUploadForm from "../../components/Forms/RecipientsMassUploadForm/RecipientsMassUploadForm";
import ActionConfirmationModal from "../../components/Modals/ActionConfirmationModal/ActionConfirmationModal";
import DecisionConfirmationModal from "../../components/Modals/DecisionConfirmationModal/DecisionConfirmationModal";
import { userProfiles } from "../../redux/slices/UserProfilesSlice";
import { useSelector, useDispatch } from "react-redux";
import { handleRoleNotAllowedRedirection } from "../../utils/userRolesUtils";
import NotAllowed from "../../components/NotAllowed/NotAllowed";
import RecipientsSearch from "../../components/RecipientsSearch/RecipientsSearch";
import {
  selectRecipientsSearch,
  setType,
  setValue,
  commitCriteria,
  setPagination,
} from "../../redux/slices/recipientsSearchSlice";

const formModalConfig = { width: "80%" };

const confirmationModalContent = {
  successAdded: { type: "success", title: "Registro añadido con éxito." },
  successMassiveAdded: {
    type: "success",
    title: "Registros añadidos con éxito.",
  },
  successModified: { type: "success", title: "Registro modificado con éxito." },
  successRemoved: { type: "success", title: "Registro eliminado con éxito." },
  errorAdded: {
    type: "error",
    title: "Error al añadir destinatario.",
    text: "Error al añadir destinatario. Por favor, inténtelo de nuevo más tarde.",
  },
  errorMassiveAdded: {
    type: "error",
    title: "Error al añadir destinatarios.",
    text: "Error al añadir destinatarios. Por favor, inténtelo de nuevo.",
  },
  errorRemoved: {
    type: "error",
    title: "Error al eliminar destinatario.",
    text: "Error al eliminar destinatario. Por favor, inténtelo de nuevo más tarde.",
  },
  errorModified: {
    type: "error",
    title: "Error al modificar destinatario.",
    text: "Error al modificar destinatario. Por favor, inténtelo de nuevo más tarde.",
  },
};

const decisionModalContent = {
  deleteRecipient: {
    title: "Eliminar destinatario",
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    text: "¿Desea eliminar al destinatario ",
  },
};

export default function Recipients() {
  let modalContent;
  const dispatch = useDispatch();

  const userRoles = useSelector(userProfiles);
  const isUserAllowed = handleRoleNotAllowedRedirection(userRoles);

  const [activeModal, setActiveModal] = useState({
    isModalActive: false,
    isModalFormActive: false,
    action: "",
    actionFunction: "",
    participantId: "",
    participantAlias: "",
    participantName: "",
    rowInfo: null,
  });

  const searchParameters = useSelector(selectRecipientsSearch);

  const {
    getPagedRecipients,
    addRecipient,
    addParticipantByFile,
    updateRecipient,
    recipientsDetails,
    setRecipientsDetails,
    deleteRecipient,
  } = useRecipients();

  const [genericModalDetails, setGenericModalDetails] = useState({
    isVisible: false,
    content: "recipientCreateForm",
    title: "",
  });

  const [confirmationModalDetails, setConfirmationModalDetails] = useState({
    isVisible: false,
    type: "",
    errorMessage: "",
  });

  const [
    decisionConfirmationModalDetails,
    setDecisionConfirmationModalDetails,
  ] = useState({
    isVisible: false,
    type: "",
  });

  const [activeRecipient, setActiveRecipient] = useState({});

  const fetchRecipients = async () => {
    await getPagedRecipients();
  };

  useEffect(() => {
    if (!isUserAllowed) return false;
    const hasCriteria = Boolean(
      searchParameters.participantName || searchParameters.participantAlias,
    );
    if (searchParameters.searchWasMade && hasCriteria) {
      getPagedRecipients(
        searchParameters.page,
        searchParameters.size,
        searchParameters.participantName,
        searchParameters.participantAlias,
        false,
      );
    } else if (!searchParameters.searchWasMade) {
      getPagedRecipients(1, searchParameters.size, null, null, false);
    }
  }, [isUserAllowed]);

  const handleSearchTypeChange = (newType) => {
    dispatch(setType(newType));
  };

  const handleSearchInputChange = (e) => {
    dispatch(setValue(e?.target?.value ?? ""));
  };

  const fetchWithCurrentCriteria = async ({ afterDelete = false } = {}) => {
    let nextPage = searchParameters.page;

    if (afterDelete) {
      const isLastItemOnPage =
        recipientsDetails?.data?.length === 1 && searchParameters.page > 1;
      if (isLastItemOnPage) nextPage = searchParameters.page - 1;
    }

    await getPagedRecipients(
      nextPage,
      searchParameters.size,
      searchParameters.participantName,
      searchParameters.participantAlias,
      false,
    );

    if (nextPage !== searchParameters.page) {
      dispatch(setPagination({ page: nextPage }));
    }
  };

  const handleSearchRecipients = async (event) => {
    event.preventDefault();

    const value = (searchParameters.value || "").trim();
    const nextParticipantName =
      searchParameters.type === "participantName" ? value || null : null;
    const nextParticipantAlias =
      searchParameters.type === "participantAlias" ? value || null : null;

    dispatch(
      commitCriteria({
        participantName: nextParticipantName,
        participantAlias: nextParticipantAlias,
      }),
    );

    await getPagedRecipients(
      1,
      searchParameters.size,
      nextParticipantName,
      nextParticipantAlias,
      false,
    );
  };

  const handleEditRecipient = async (recipientDetails) => {
    try {
      handleToggleGenericModal();
      const updatedRecipient = await updateRecipient(recipientDetails);

      if (updatedRecipient?.apierror) {
        return handleToggleConfirmationModal(
          "errorModified",
          updatedRecipient.apierror.message,
        );
      }

      if (updatedRecipient) {
        await fetchWithCurrentCriteria();
        return handleToggleConfirmationModal("successModified");
      }
      handleToggleConfirmationModal("errorModified");
    } catch (error) {
      console.error(error);
      handleToggleConfirmationModal("errorModified");
    }
  };

  const handleDeleteRecipient = async () => {
    try {
      handleToggleDecisionConfirmationModal();
      const deletedRecipient = await deleteRecipient(
        activeRecipient.participantId,
      );

      if (deletedRecipient?.apierror) {
        return handleToggleConfirmationModal(
          "errorRemoved",
          deletedRecipient.apierror.message,
        );
      }

      if (deletedRecipient) {
        await fetchWithCurrentCriteria({ afterDelete: true });
        return handleToggleConfirmationModal("successRemoved");
      }

      handleToggleConfirmationModal("errorRemoved");
    } catch (error) {
      console.error(error);
      handleToggleDecisionConfirmationModal("errorRemoved");
    }
  };

  const handleAddRecipient = async (recipientDetails) => {
    try {
      handleToggleGenericModal();
      const addedRecipient = await addRecipient(recipientDetails, false);
      if (addedRecipient?.status === "ok") {
        await getPagedRecipients(
          searchParameters.page,
          searchParameters.size,
          searchParameters.participantName,
          searchParameters.participantAlias,
          false,
        );
        return handleToggleConfirmationModal("successAdded");
      }
      handleToggleConfirmationModal("errorAdded");
    } catch (error) {
      console.error(error);
      handleToggleConfirmationModal("errorAdded");
    }
  };

  const handleAddRecipientsByFile = async (filePayload) => {
    try {
      const result = await addParticipantByFile(filePayload, true);
      if (result?.status === "ok") {
        await fetchWithCurrentCriteria();
        actions.toggleGenericModal();
        return handleToggleConfirmationModal("successMassiveAdded");
      }
      handleToggleConfirmationModal("errorMassiveAdded");
    } catch (error) {
      console.error(error);
      handleToggleConfirmationModal("errorMassiveAdded");
    }
  };

  const handleToggleGenericModal = (content, activeRecipientDetails) => {
    if (!content) {
      return setGenericModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
      }));
    }

    if (activeRecipientDetails) {
      setActiveRecipient(activeRecipientDetails);
    }

    setGenericModalDetails((prevState) => ({
      content: content,
      isVisible: !prevState?.isVisible,
    }));
  };

  const handleSaveForm = () => {
    handleToggleGenericModal();
  };

  const handleToggleConfirmationModal = (type, apierror) => {
    if (!type) {
      return setConfirmationModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
        errorMessage: "",
      }));
    }

    setConfirmationModalDetails((prevState) => ({
      ...prevState,
      type: type,
      isVisible: !prevState.isVisible,
      errorMessage: apierror,
    }));
  };

  const handleToggleDecisionConfirmationModal = (activeRecipientDetails) => {
    if (!activeRecipientDetails) {
      return setDecisionConfirmationModalDetails((prevState) => ({
        ...prevState,
        isVisible: !prevState.isVisible,
      }));
    }
    setActiveRecipient(activeRecipientDetails);
    return setDecisionConfirmationModalDetails((prevState) => ({
      ...prevState,
      isVisible: !prevState.isVisible,
      type: "deleteRecipient",
    }));
  };

  const actions = {
    addRecipient: handleAddRecipient,
    editRecipient: handleEditRecipient,
    deleteRecipient: handleDeleteRecipient,
    setGenericModalDetails: setGenericModalDetails,
    toggleGenericModal: handleToggleGenericModal,
    toggleDecisionConfirmationModal: handleToggleDecisionConfirmationModal,
    setActiveRecipient: setActiveRecipient,
    save: handleSaveForm,
    getPagedRecipients: getPagedRecipients,
    setRecipientsDetails: setRecipientsDetails,
    addParticipantByFile: addParticipantByFile,
  };

  if (genericModalDetails?.content === "recipientEditForm") {
    modalContent = (
      <RecipientForm
        activeRecipient={activeRecipient}
        actions={actions}
        formType="recipientEditForm"
        callback={handleEditRecipient}
      />
    );
  }

  if (genericModalDetails?.content === "recipientCreateForm") {
    modalContent = (
      <RecipientForm
        actions={actions}
        formType="recipientCreateForm"
        callback={handleAddRecipient}
      />
    );
  }

  if (genericModalDetails?.content === "recipientsMassUploadForm") {
    modalContent = (
      <RecipientsMassUploadForm
        actions={actions}
        callback={handleAddRecipientsByFile}
      />
    );
  }

  if (!isUserAllowed) {
    return (
      <NotAllowed
        isUserAllowed={isUserAllowed}
        isLoading={userRoles?.isLoading}
      />
    );
  }

  const modalWidth =
    genericModalDetails?.content === "recipientsMassUploadForm" ? "30%" : "80%";

  return (
    <div className="Recipients">
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 25,
          alignItems: "center",
        }}
      >
        <h2>Destinatarios</h2>

        <div style={{ display: "flex", gap: 10 }} className="search-grid-row">
          <Button
            onClick={() => actions.toggleGenericModal("recipientCreateForm")}
            className="add-btn"
          >
            Añadir destinatario
          </Button>
          <Button
            className="add-btn"
            onClick={() =>
              actions.toggleGenericModal("recipientsMassUploadForm")
            }
          >
            Carga masiva
          </Button>
        </div>
      </div>

      <RecipientsSearch
        searchParameters={searchParameters}
        onTypeChange={handleSearchTypeChange}
        onValueChange={handleSearchInputChange}
        onSubmit={handleSearchRecipients}
      />

      {searchParameters.searchWasMade && (
        <RecipientsTable
          recipientsDetails={recipientsDetails}
          tableActions={actions}
        />
      )}

      <GenericFormModal
        title={genericModalDetails.title}
        isVisible={genericModalDetails?.isVisible}
        actions={actions}
        modalConfig={{ ...formModalConfig, width: modalWidth }}
      >
        {modalContent}
      </GenericFormModal>

      <ActionConfirmationModal
        modalDetails={confirmationModalDetails}
        callback={handleToggleConfirmationModal}
        modalContent={confirmationModalContent}
      />

      <DecisionConfirmationModal
        modalDetails={decisionConfirmationModalDetails}
        modalContent={decisionModalContent}
        callback={handleDeleteRecipient}
        toggleModal={handleToggleDecisionConfirmationModal}
        activeParticipant={activeRecipient}
      />
    </div>
  );
}
